import { Card } from "@/components/ui/Card";
import { VoteButtons } from "@/components/ui/VoteButtons";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type { WeekendSuggestion, MemberProfile } from "@/lib/models";
import { Link } from "react-router-dom";
import { useState } from "react";

const TIER_LABELS: Record<string, string> = {
  at_home: "At Home",
  local: "Local",
  day_trip: "Day Trip",
  getaway: "Getaway",
};

interface WeekendCardProps {
  suggestion: WeekendSuggestion;
  userId?: string;
  onVote: (suggestion: WeekendSuggestion, isUpvote: boolean) => void;
  /** Compact variant for dashboard grid */
  variant?: "compact" | "full";
  /** Full variant extras */
  isAdult?: boolean;
  onChoose?: (suggestion: WeekendSuggestion) => void;
  findMember?: (uid: string) => MemberProfile | undefined;
}

export function WeekendCard({
  suggestion: s,
  userId,
  onVote,
  variant = "full",
  isAdult,
  onChoose,
}: WeekendCardProps) {
  const [expandedPacking, setExpandedPacking] = useState(false);

  const votes = s.votes ?? {};
  const upvotes = Object.values(votes).filter((v) => v === true).length;
  const downvotes = Object.values(votes).filter((v) => v === false).length;
  const userVote = userId ? votes[userId] : undefined;
  const isFavorite = !s.is_chosen && upvotes > downvotes && upvotes > 0;

  if (variant === "compact") {
    return (
      <Card className={s.is_chosen ? "ring-2 ring-green-400 dark:ring-green-500" : undefined}>
        <Link to="/weekend" className="block space-y-1.5">
          <div className="flex items-start justify-between gap-1">
            <StatusBadge text={TIER_LABELS[s.tier] ?? s.tier} preset={s.tier} />
            {s.is_chosen ? (
              <StatusBadge text="This weekend's pick" preset="chosen" />
            ) : isFavorite ? (
              <StatusBadge text="★" preset="custom" />
            ) : null}
          </div>
          <p className="font-semibold text-xs line-clamp-2">{s.title}</p>
          {s.description && (
            <p className="text-xs text-gray-400 line-clamp-1">{s.description}</p>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            {upvotes > 0 && <span>👍 {upvotes}</span>}
            {downvotes > 0 && <span>👎 {downvotes}</span>}
          </div>
        </Link>
      </Card>
    );
  }

  // Full variant
  return (
    <Card className={s.is_chosen ? "ring-2 ring-green-400 dark:ring-green-500" : undefined}>
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <div className="flex flex-wrap gap-1.5">
            <StatusBadge text={TIER_LABELS[s.tier] ?? s.tier} preset={s.tier} />
            {s.source === "custom" && <StatusBadge text="Custom" preset="custom" />}
            {s.weather_dependent && <StatusBadge text="🌤️ Weather" preset="weather" />}
            {s.is_chosen && <StatusBadge text="✅ This weekend's pick" preset="chosen" />}
            {!s.is_chosen && isFavorite && <StatusBadge text="★ Favorite" preset="custom" />}
          </div>
        </div>

        <p className="font-semibold text-sm">{s.title}</p>
        {s.description && <p className="text-xs text-gray-500">{s.description}</p>}

        {(s.estimated_cost || s.duration || s.best_for) && (
          <div className="flex flex-wrap gap-3 text-xs text-gray-400">
            {s.estimated_cost && <span>💲 {s.estimated_cost}</span>}
            {s.duration && <span>⏱ {s.duration}</span>}
            {s.best_for && <span>👥 {s.best_for}</span>}
          </div>
        )}

        {s.tags && s.tags.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {s.tags.map((tag) => (
              <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Packing list */}
        {s.packing_list && s.packing_list.length > 0 && (
          <div>
            <button
              onClick={() => setExpandedPacking(!expandedPacking)}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${expandedPacking ? "rotate-90" : ""}`}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Packing list ({s.packing_list.length})
            </button>
            {expandedPacking && (
              <ul className="mt-1 ml-4 space-y-0.5">
                {s.packing_list.map((item, i) => (
                  <li key={i} className="text-xs text-gray-500 list-disc">{item}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Vote + Choose row */}
        <div className="flex items-center gap-2 pt-1.5 border-t border-gray-100 dark:border-white/10">
          <VoteButtons
            upvotes={upvotes}
            downvotes={downvotes}
            userVote={userVote}
            onVote={(isUp) => onVote(s, isUp)}
          />

          <div className="flex-1" />

          {isAdult && onChoose && (
            <button
              onClick={() => onChoose(s)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                s.is_chosen
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/15"
              }`}
            >
              {s.is_chosen ? "Chosen ✓" : "Make this our weekend plan"}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
